import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useListSessionsQuery } from '@/features/admin/api/sessionsApi';
import { useGetAdminsQuery } from '@/features/admin/api/adminApi';

export function useRecentCalls() {
  const navigate = useNavigate();
  const [page, setPage] = useState(1);
  const [limit] = useState(10);
  const [searchQuery, setSearchQuery] = useState('');
  const [days, setDays] = useState<number | undefined>(undefined);

  const adminUser = JSON.parse(localStorage.getItem('admin_user') || '{}');
  const userId = adminUser.id || adminUser.userId || adminUser._id;

  const isOrgAdmin = adminUser.role === 'Admin' || adminUser.role === 'SuperAdmin' || adminUser.orgRole === 'admin';

  // Org Members (non-admins) can only view their own calls.
  // Org Admins default to viewing all team members' calls (undefined).
  const [selectedUserId, setSelectedUserId] = useState<string | undefined>(
    isOrgAdmin ? undefined : userId
  );

  const { data: teamData } = useGetAdminsQuery(undefined, { skip: !isOrgAdmin });
  const teamMembers = teamData?.data || [];

  const effectiveUserId = isOrgAdmin ? selectedUserId : userId;

  // Fully backend-driven pagination, filtering, and search query
  const { data: sessionsResponse, isLoading, isFetching, refetch } = useListSessionsQuery({
    page,
    limit,
    user_id: effectiveUserId,
    days,
    search: searchQuery.trim() ? searchQuery.trim() : undefined
  });

  const sessions = sessionsResponse?.data?.data || [];
  const pagination = sessionsResponse?.data?.pagination || {
    total: 0,
    page: 1,
    limit: 10,
    totalPages: 1
  };

  const handlePageChange = (newPage: number) => {
    if (newPage >= 1 && newPage <= pagination.totalPages) {
      setPage(newPage);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const handleSearchChange = (query: string) => {
    setSearchQuery(query);
    setPage(1);
  };

  const handleDaysChange = (newDays: number | undefined) => {
    setDays(newDays);
    setPage(1);
  };

  const handleUserChange = (newUserId: string | undefined) => {
    setSelectedUserId(newUserId);
    setPage(1);
  };

  const handleSelectCall = (id: string) => {
    navigate(`/admin/sessions/${id}`);
  };

  return {
    sessions,
    pagination,
    isLoading,
    isFetching,
    page,
    searchQuery,
    setSearchQuery: handleSearchChange,
    days,
    setDays: handleDaysChange,
    selectedUserId: effectiveUserId,
    setSelectedUserId: handleUserChange,
    isOrgAdmin,
    teamMembers,
    handlePageChange,
    handleSelectCall,
    refetch
  };
}
